import { Injectable } from '@angular/core';
import S3 from 'aws-sdk/clients/s3';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject, Subject } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';

import { DocumentItem } from '@app/shared/document-item';

import { User } from '../shared/user';
import { AnalyticsService } from './analytics.service';
import { ApiService } from './api.service';
import { FeatureFlags, FeatureFlagVariants } from './feature-flags/feature-flags';
import { LaunchDarklyService } from './feature-flags/launchdarkly.service';
import { UploadData } from './upload-data';
import { UserService } from './user.service';

export class ApiStatus {
  static NOT_READY = 'NOT_READY';
  static READY = 'READY';
}

@Injectable()
export class AwsService {
  private static LINK_EXPIRATION_TIME = 60;
  private s3: S3;
  private sessionExpiresAt: Date;
  private bucket: string;
  private preScanBucket?: string;
  private currentUser: User;
  private uploadPathRoot: string;
  apiStatusStream: BehaviorSubject<string>;
  viewStream: ReplaySubject<DocumentItem>;
  downloadStream: ReplaySubject<DocumentItem>;

  constructor(
    private apiService: ApiService,
    private userService: UserService,
    private analyticsService: AnalyticsService,
    private launchDarklyService: LaunchDarklyService,
  ) {
    this.apiStatusStream = new BehaviorSubject(ApiStatus.NOT_READY);
    this.viewStream = new ReplaySubject<DocumentItem>();
    this.downloadStream = new ReplaySubject<DocumentItem>();
    this.userService.user$.subscribe(user => {
      this.currentUser = user;
      this.getToken();
    });

    this.userService.getUser();

    this.initViewAction();
    this.initDownloadAction();
  }

  view(document: DocumentItem) {
    this.checkAndUpdateToken();
    this.viewStream.next(document);
  }

  download(document: DocumentItem) {
    this.checkAndUpdateToken();
    this.downloadStream.next(document);
  }

  upload(key: string, file: File): Subject<UploadData> {
    this.checkAndUpdateToken();
    const uploadStream = new Subject<UploadData>();

    this.launchDarklyService
      .featureFlag$<FeatureFlagVariants>(FeatureFlags.ENABLE_PRESIGNED_URL_PATIENT_UI, FeatureFlagVariants.OFF)
      .pipe(
        take(1),
        switchMap(flagVariant => {
          if (flagVariant === FeatureFlagVariants.ON_VARIANT && !this.preScanBucket) {
            return new Observable<UploadData>(observer => {
              observer.error(new Error('Upload bucket is not defined'));
            });
          }

          return this.apiStatusStream.pipe(
            filter(status => status === ApiStatus.READY),
            switchMap(() => this.removeNonAsciiCharacters$(key)),
            switchMap(fileKey => {
              if (flagVariant === FeatureFlagVariants.ON_VARIANT) {
                return this.presignedUrlUpload(fileKey, file);
              } else {
                return this.directS3Upload(fileKey, file);
              }
            }),
          );
        }),
      )
      .subscribe({
        next: data => uploadStream.next(data),
        error: error => uploadStream.error(error),
        complete: () => uploadStream.complete(),
      });

    return uploadStream;
  }

  private checkAndUpdateToken() {
    if (this.sessionExpiresAt && Date.now() >= this.sessionExpiresAt.getTime()) {
      this.apiStatusStream.next(ApiStatus.NOT_READY);
      this.getToken();
    }
  }

  private getToken() {
    this.apiService.post('/api/v2/aws_session', null).subscribe((response: any) => {
      this.sessionExpiresAt = new Date(response.expires_at);
      this.bucket = response.bucket;
      this.preScanBucket = response.pre_scan_bucket;
      this.uploadPathRoot = `patients/${this.currentUser.id}`;
      this.s3 = new S3({
        region: 'us-east-1',
        credentials: {
          accessKeyId: response.credentials.access_key_id,
          secretAccessKey: response.credentials.secret_access_key,
          sessionToken: response.credentials.session_token,
        },
        signatureVersion: 'v4',
      });
      this.apiStatusStream.next(ApiStatus.READY);
    });
  }

  private getPresignedUrl(
    bucket: string,
    key: string,
    operation: string,
    contentDisposition?: string,
  ): Observable<string> {
    return this.apiService
      .post('/api/v2/presigned_urls', {
        bucket,
        object_key: key,
        operation,
        content_disposition: contentDisposition,
      })
      .pipe(map((response: any) => response.presigned_url));
  }

  private initViewAction() {
    combineLatest([this.viewStream, this.apiStatusStream])
      .pipe(filter(vals => vals[1] === ApiStatus.READY))
      .subscribe(vals => {
        const document: DocumentItem = vals[0];
        this.launchDarklyService
          .featureFlag$<FeatureFlagVariants>(FeatureFlags.ENABLE_PRESIGNED_URL_PATIENT_UI, FeatureFlagVariants.OFF)
          .pipe(take(1))
          .subscribe(flagVariant => {
            if (flagVariant === FeatureFlagVariants.ON_VARIANT) {
              this.getPresignedUrl(document.bucket, document.key, 'get', 'inline').subscribe({
                next: url => {
                  if (url) {
                    window.open(url);
                  }
                },
                error: err => console.error('Error getting view URL:', err),
              });
            } else {
              this.getSignedUrl(document, 'inline', (err: Error, url: string) => {
                if (url) {
                  window.open(url);
                }
              });
            }
          });
        this.analyticsService.attachmentViewed(document);
      });
  }

  private initDownloadAction() {
    combineLatest([this.downloadStream, this.apiStatusStream])
      .pipe(filter(vals => vals[1] === ApiStatus.READY))
      .subscribe(vals => {
        const document: DocumentItem = vals[0];
        this.launchDarklyService
          .featureFlag$<FeatureFlagVariants>(FeatureFlags.ENABLE_PRESIGNED_URL_PATIENT_UI, FeatureFlagVariants.OFF)
          .pipe(take(1))
          .subscribe(flagVariant => {
            if (flagVariant === FeatureFlagVariants.ON_VARIANT) {
              this.getPresignedUrl(document.bucket, document.key, 'get', 'attachment').subscribe({
                next: url => {
                  if (url) {
                    window.location.href = url;
                  }
                },
                error: err => console.error('Error getting download URL:', err),
              });
            } else {
              this.getSignedUrl(document, 'attachment', (err: Error, url: string) => {
                if (url) {
                  window.location.href = url;
                }
              });
            }
          });
        this.analyticsService.attachmentDownloaded(document);
      });
  }

  private getSignedUrl(document: DocumentItem, contentDisposition: string, handler: (err: Error, url: string) => void) {
    this.s3.getSignedUrl(
      'getObject',
      {
        Bucket: document.bucket,
        Key: document.key,
        Expires: AwsService.LINK_EXPIRATION_TIME,
        ResponseContentDisposition: contentDisposition,
      },
      handler,
    );
  }

  private presignedUrlUpload(fileKey: string, file: File): Observable<UploadData> {
    const fullKey = `${this.uploadPathRoot}/${fileKey}`;
    return this.getPresignedUrl(this.preScanBucket, fullKey, 'put').pipe(
      switchMap(presignedUrl => this.uploadWithPresignedUrl(presignedUrl, file, fullKey)),
    );
  }

  private directS3Upload(fileKey: string, file: File): Observable<UploadData> {
    const fullKey = `${this.uploadPathRoot}/${fileKey}`;
    return new Observable<UploadData>(observer => {
      const params = {
        Key: fullKey,
        Body: file,
        Bucket: this.preScanBucket,
        ContentType: file.type,
        ServerSideEncryption: 'AES256',
      };

      const options = {};

      this.s3
        .upload(params, options, (error, data) => {
          if (error) {
            observer.error(error);
          } else {
            observer.next(new UploadData(true, 100, data.Key, this.bucket));
            observer.complete();
          }
        })
        .on('httpUploadProgress', event => {
          observer.next(new UploadData(false, Math.round((100 * event.loaded) / event.total)));
        });
    });
  }

  private uploadWithPresignedUrl(presignedUrl: string, file: File, fullKey: string): Observable<UploadData> {
    return new Observable(observer => {
      const request = new XMLHttpRequest();

      request.upload.onprogress = event => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          observer.next(new UploadData(false, percentComplete));
        }
      };

      request.onload = () => {
        const error = request.status < 200 || request.status >= 300;
        if (!error) {
          observer.next(new UploadData(true, 100, fullKey, this.bucket));
          observer.complete();
        } else {
          observer.error(new Error(`HTTP Error: ${request.status}`));
        }
      };

      request.onerror = () => {
        observer.error(new Error('Network Error'));
      };

      request.open('PUT', presignedUrl);
      request.setRequestHeader('Content-Type', file.type);
      request.send(file);

      return () => {
        request.abort();
      };
    });
  }

  private removeNonAsciiCharacters$(key: string): Observable<string> {
    return this.launchDarklyService.featureFlag$(FeatureFlags.ENABLE_UPLOADED_FILE_NAME_SANITIZATION, false).pipe(
      map(enabled => {
        if (!enabled) {
          return key;
        }

        return key.replace(/[^\x00-\x7F]/g, '');
      }),
      take(1),
    );
  }
}
